import React from 'react';

import { Box, Grid, inputClasses, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { useAttestationsListQuery } from 'src/api';
import { Button, FullscreenLoader } from 'src/components/common';
import { CheckboxField, TextField } from 'src/components/finalForm';
import { AttestationPurpose } from 'src/constants/attestation';
import { useAuthSignup } from 'src/hooks/auth';
import { SxProp } from 'src/types/mui';
import { shouldDisplayError } from 'src/utils/shouldDisplayError';

import { RenderErrorMessage } from '../../RenderErrorMessage';
import { peoAttestationsFormValidationSchema } from '../utils';

export const PeoAttestationsForm: React.FC = () => {
  const { goToPreviousStep, goToNextStep } = useAuthSignup();
  const { data, loading } = useAttestationsListQuery();

  const attestations = data?.attestationList.filter(attestation =>
    attestation.purpose?.includes(AttestationPurpose.forPeo),
  );

  const renderForm = () => {
    if (loading) {
      return <FullscreenLoader />;
    }
    return (
      <Form
        subscription={{
          valid: true,
          submitting: true,
          errors: true,
          dirtyFields: true,
        }}
        onSubmit={() => goToNextStep()}
        validate={validateWithYupSchema(peoAttestationsFormValidationSchema(attestations))}
      >
        {({ submitting, valid, errors, dirtyFields }) => (
          <Grid
            sx={{
              pt: '15px',
              maxHeight: '459px',
              overflow: 'auto',
            }}
            size={12}
          >
            {attestations?.map(fields => (
              <React.Fragment key={fields.id}>
                <Field
                  fullWidth
                  name={fields.name}
                  multiline
                  label={fields.name}
                  component={TextField}
                  type="text"
                  disabled
                  showErrorWhenDisabled={false}
                  sx={fieldStyles}
                  slotProps={{
                    input: {
                      value: fields.text,
                      readOnly: true,
                      rows: 0,
                      sx: inputStyles,
                    },
                  }}
                />
                <Box
                  display="flex"
                  justifyContent="space-between"
                  mt={3}
                  mb={shouldDisplayError(fields.name, errors, dirtyFields) ? 0 : 3}
                >
                  <Field
                    component={CheckboxField}
                    name={fields.name}
                    label={`I have read and agreed to ${fields.name}`}
                    fullWidth
                    required
                  />
                  <Button
                    variant="contained"
                    type="button"
                    component="a"
                    href={fields.pdf?.downloadUrl as string}
                    target="_blank"
                    size="small"
                    sx={{ whiteSpace: 'nowrap' }}
                    color="primary"
                  >
                    See more
                  </Button>
                </Box>
                {shouldDisplayError(fields.name, errors, dirtyFields) && (
                  <RenderErrorMessage errors={errors} field={fields.name} />
                )}
              </React.Fragment>
            ))}
            <Box display="flex" justifyContent="center" mt={4} gap={4}>
              <Button
                type="button"
                disabled={submitting}
                size="large"
                variant="outlined"
                color="error"
                onClick={() => goToPreviousStep()}
                sx={{ textTransform: 'uppercase' }}
              >
                I disagree
              </Button>
              <Button
                type="submit"
                disabled={!valid || submitting}
                loading={submitting}
                size="large"
                variant="contained"
                sx={{ textTransform: 'uppercase' }}
              >
                I agree
              </Button>
            </Box>
          </Grid>
        )}
      </Form>
    );
  };

  return (
    <Box>
      <Typography variant="body2" fontSize="24px">
        Attestations
      </Typography>

      <Typography variant="subtitle1" fontSize="16px" mt={1} mb={5} color="text.secondary">
        Read and agree to following to activate your account
      </Typography>
      {renderForm()}
    </Box>
  );
};

const fieldStyles: SxProp = {
  userSelect: 'none',
  pointerEvents: 'none',
};

const inputStyles: SxProp = {
  [`&.${inputClasses.disabled}`]: {
    textarea: {
      '-webkit-text-fill-color': '#000000',
    },
  },
};
